import { useContext, useState, useEffect } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";

import MUIButton from "@mui/material/Button";
import MUIGrid from "@mui/material/Grid";
import MUITextField from "@mui/material/TextField";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIBox from "@mui/material/Box";
import MUIContentCopyIcon from "@mui/icons-material/ContentCopy";
import MUILaunchIcon from "@mui/icons-material/Launch";
import MUIListItemIcon from "@mui/material/ListItemIcon";

import Menu from "../../Menu";
import Card from "../../Card";
import Security from "../../../Utility/Security";
import InfoItem from "../../InfoItem";
import api from "../../../Seating/Security/api";

import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";

/**
 * CardChartExternalIds renders a Card component
 * displaying patient external ID information.
 * @returns {JSX.Element}
 */

const title = "External IDs";
const icon = MUILaunchIcon;

const className = "CardChartExternalIds";

export default function CardChartExternalIds(props) {
  const chartContext = useContext(ChartContext);
  const patient = chartContext.chart.patient[chartContext.patientId];
  const order = chartContext.chart.orderActivity[chartContext.orderId] ?? null;
  const originalPatient =
    chartContext.originalChart.patient[chartContext.patientId];
  const originalOrder =
    chartContext.originalChart.orderActivity[chartContext.orderId];
  const canEditExternalIds = order
    ? Security.hasPermission("orderActivity.updateExternalIds")
    : Security.hasPermission("patient.updateExternalIds");
  const [editMode, setEditMode] = useState(false);
  const [cardChart, setCardChart] = useState(null);
  const [orderChart, setOrderChart] = useState(null);

  // Close edit mode if originalChart changes (on save)
  useEffect(() => {
    setEditMode(false);
  }, [chartContext.originalChart]);

  // Sync this card's edit mode with ChartContext for ChartNavigationBar display
  useEffect(() => {
    chartContext.setCardEditMode?.(className, editMode);
    return () => chartContext.setCardEditMode?.(className, false);
  }, [editMode, chartContext.setCardEditMode]);

  /**
   * Returns the styles for a container that should be full width at a given max width.
   * Only used when sidebar=true to handle responsive container queries.
   * @param {number} maxWidth - The max width at which the container should be full width.
   * @returns {Object} The styles for the container.
   */
  const getContainerQuery = (maxWidth) => {
    if (!props.sidebar) {
      return {};
    }

    return {
      [`@container (max-width: ${maxWidth}px)`]: {
        flexBasis: "100% !important",
        maxWidth: "100% !important",
      },
    };
  };

  /**
   * Renders the external ID section of the order external IDs.
   * @returns {JSX.Element} Grid layout with external ID information display
   */
  const renderExternalIdSectionOrder = () => {
    return (
      <MUIGrid item xs={12}>
        <MUIGrid container spacing={2}>
          <MUIGrid item lg={3} md={6} xs={12} sx={getContainerQuery(600)}>
            <MUITextField
              value={
                order && order.brightreeExternalId
                  ? order.brightreeExternalId
                  : ""
              }
              size="small"
              label="Brightree ID"
              variant="standard"
              fullWidth={true}
              inputProps={{
                maxLength: 7,
              }}
              InputLabelProps={{ shrink: true }}
              onBlur={(e) =>
                chartContext.onChange({
                  orderActivity: {
                    [chartContext.orderId]: {
                      brightreeExternalId: parseInt(e.target.value.trim()),
                    },
                  },
                })
              }
              onChange={(e) => {
                const cleaned = parseInt(e.target.value.replace(/\D/g, ""));
                chartContext.onChange({
                  orderActivity: {
                    [chartContext.orderId]: {
                      brightreeExternalId: isNaN(cleaned) ? null : cleaned,
                    },
                  },
                });
                setOrderChart({
                  ...orderChart,
                  brightreeExternalId: isNaN(cleaned) ? null : cleaned,
                });
              }}
            />
          </MUIGrid>
        </MUIGrid>
      </MUIGrid>
    );
  };

  /**
   * Renders the external ID section of the patient external IDs.
   * @returns {JSX.Element} Grid layout with external ID information display
   */
  const renderExternalIdSectionPatient = () => {
    return (
      <MUIGrid item xs={12}>
        <MUIGrid container spacing={2}>
          <MUIGrid item lg={3} md={6} xs={12} sx={getContainerQuery(600)}>
            <MUITextField
              value={
                patient && patient.brightreeExternalKey
                  ? patient.brightreeExternalKey
                  : ""
              }
              autoFocus={true}
              size="small"
              label="Brightree Patient Key"
              variant="standard"
              fullWidth={true}
              inputProps={{
                maxLength: 7,
              }}
              InputLabelProps={{ shrink: true }}
              onBlur={(e) =>
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: {
                      brightreeExternalKey:
                        parseInt(e.target.value.trim()) || null,
                    },
                  },
                })
              }
              onChange={(e) => {
                const cleaned = parseInt(e.target.value.replace(/\D/g, ""));
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: {
                      brightreeExternalKey: cleaned || null,
                    },
                  },
                });
                setCardChart({
                  ...cardChart,
                  brightreeExternalKey: cleaned || null,
                });
              }}
            />
          </MUIGrid>
          <MUIGrid item lg={3} md={6} xs={12} sx={getContainerQuery(600)}>
            <MUITextField
              value={
                patient && patient.brightreeExternalId
                  ? patient.brightreeExternalId
                  : ""
              }
              size="small"
              label="Brightree Patient ID"
              variant="standard"
              fullWidth={true}
              inputProps={{
                maxLength: 7,
              }}
              InputLabelProps={{ shrink: true }}
              onBlur={(e) =>
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: {
                      brightreeExternalId:
                        parseInt(e.target.value.trim()) || null,
                    },
                  },
                })
              }
              onChange={(e) => {
                const cleaned = e.target.value.replace(/\D/g, "").slice(0, 7);
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: {
                      brightreeExternalId: parseInt(cleaned) || null,
                    },
                  },
                });
                setCardChart({
                  ...cardChart,
                  brightreeExternalId: parseInt(cleaned) || null,
                });
              }}
            />
          </MUIGrid>
          <MUIGrid item lg={3} md={6} xs={12} sx={getContainerQuery(600)}>
            <MUITextField
              value={
                patient && patient.birdeyeExternalId
                  ? patient.birdeyeExternalId
                  : ""
              }
              size="small"
              label="Birdeye Patient ID"
              variant="standard"
              fullWidth={true}
              inputProps={{
                maxLength: 10,
              }}
              InputLabelProps={{ shrink: true }}
              onBlur={(e) =>
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: {
                      birdeyeExternalId:
                        parseInt(e.target.value.trim()) || null,
                    },
                  },
                })
              }
              onChange={(e) => {
                const cleaned = e.target.value.replace(/\D/g, "").slice(0, 10);
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: {
                      birdeyeExternalId: parseInt(cleaned) || null,
                    },
                  },
                });
                setCardChart({
                  ...cardChart,
                  birdeyeExternalId: parseInt(cleaned) || null,
                });
              }}
            />
          </MUIGrid>
        </MUIGrid>
      </MUIGrid>
    );
  };

  /**
   * Renders the edit view with form fields for editing patient or order external ids.
   * @returns {JSX.Element} Grid layout with editable form fields
   */
  const renderEditView = () => {
    return (
      <MUIGrid
        container
        spacing={4}
        rowSpacing={4}
        sx={{ containerType: "inline-size" }}
      >
        {order
          ? renderExternalIdSectionOrder()
          : renderExternalIdSectionPatient()}
      </MUIGrid>
    );
  };

  /**
   * Renders the read-only view displaying order external id information.
   * @returns {JSX.Element} Grid layout with order information display
   */
  const renderReadOnlyViewOrder = () => {
    return (
      <MUIGrid container spacing={8} rowSpacing={2}>
        <MUIGrid item xs={"auto"} sx={{ display: "flex" }}>
          <InfoItem
            label={"Brightree ID"}
            value={
              <>
                {order.brightreeExternalId !== null &&
                order.brightreeExternalId !== ""
                  ? order.brightreeExternalId
                  : "None"}
              </>
            }
          />
        </MUIGrid>
      </MUIGrid>
    );
  };

  /**
   * Renders the read-only view displaying patient external id information.
   * @returns {JSX.Element} Grid layout with patient information display
   */
  const renderReadOnlyViewPatient = () => {
    return (
      <MUIGrid container spacing={4} rowSpacing={2}>
        <MUIGrid item xs={"auto"} sx={{ display: "flex" }}>
          <InfoItem
            label={"Brightree Patient Key"}
            value={
              <>
                {patient.brightreeExternalKey === null ||
                patient.brightreeExternalKey === ""
                  ? "None"
                  : patient.brightreeExternalKey}
              </>
            }
          />
        </MUIGrid>
        <MUIGrid item xs={"auto"} sx={{ display: "flex" }}>
          <InfoItem
            label={"Brightree Patient ID"}
            value={
              <>
                {patient.brightreeExternalId !== null &&
                patient.brightreeExternalId !== ""
                  ? patient.brightreeExternalId
                  : "None"}
              </>
            }
          />
        </MUIGrid>
        {patient.birdeyeExternalId !== null &&
          patient.birdeyeExternalId !== "" && (
            <MUIGrid item xs={"auto"} sx={{ display: "flex" }}>
              <InfoItem
                label={"Birdeye ID"}
                value={patient.birdeyeExternalId}
              />
            </MUIGrid>
          )}
      </MUIGrid>
    );
  };

  /**
   * Determines which view to render based on edit mode state.
   * @returns {JSX.Element} Either edit view or read-only view
   */
  const renderContent = () => {
    if (editMode) {
      return renderEditView();
    }
    return (
      <>{order ? renderReadOnlyViewOrder() : renderReadOnlyViewPatient()}</>
    );
  };

  /**
   * Returns the action buttons for the card component.
   * @returns {Array} Array of action button components
   */
  const handleCancel = () => {
    if (order) {
      const updatedOrder = { ...order };
      const orderFields = orderChart ? Object.keys(orderChart) : [];
      for (const field of orderFields) {
        if (order[field] !== originalOrder[field]) {
          updatedOrder[field] = originalOrder[field];
        }
      }
      chartContext.onChange({
        orderActivity: { [chartContext.orderId]: { ...updatedOrder } },
      });
      chartContext.setCardEditMode?.(className, false);
      setEditMode(false);

      const clearedIssues = { ...chartContext.validationIssues };
      delete clearedIssues[className];
      chartContext.updateValidationIssues(clearedIssues);
    } else {
      const updatedPatient = { ...patient };
      const externalIdFields = cardChart ? Object.keys(cardChart) : [];

      for (const field of externalIdFields) {
        if (patient[field] !== originalPatient[field]) {
          updatedPatient[field] = originalPatient[field];
        }
      }
      chartContext.onChange({
        patient: { [chartContext.patientId]: { ...updatedPatient } },
      });
      chartContext.setCardEditMode?.(className, false);
      setEditMode(false);

      const clearedIssues = { ...chartContext.validationIssues };
      delete clearedIssues[className];
      chartContext.updateValidationIssues(clearedIssues);
    }
  };

  const getActions = () => {
    return [
      <MUIBox>
        {editMode
          ? [
              <MUIButton
                color="error"
                key="cancel"
                disabled={chartContext.isUpdating}
                onClick={handleCancel}
                size="small"
              >
                Cancel
              </MUIButton>,
            ]
          : [
              ...(canEditExternalIds
                ? [
                    <MUIButton
                      key="edit"
                      disabled={chartContext.isUpdating}
                      onClick={() => setEditMode(true)}
                      size="small"
                    >
                      Edit
                    </MUIButton>,
                  ]
                : []),
            ]}
      </MUIBox>,
    ];
  };

  return (
    <Card
      renderContent={renderContent}
      getActions={getActions}
      getContainerType={() => "inline-block"}
      getTitle={() => title}
      getIcon={() => icon}
    />
  );
}

CardChartExternalIds.meta = { className: className, title: title, icon: icon };
